import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SAGA_ACTIONS } from "src/_config";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

interface FilterState {
  exp_min: string;
  exp_max: string;
  industry_ids: number[];
  country_id: string;
  city_id: string;
  education_ids: number[];
  language_ids: number[];
  nationality_ids: number[];
  gender_id: string;
  cv_completeness: string;
  last_updated: string;
  last_visited: string;
  has_video_cv: boolean;
}

interface CandidateAdvancedFiltersProps {
  onApplyFilters: (filters: FilterState) => void;
  onResetFilters: () => void;
  onFilterChange?: (filters: FilterState) => void;
}

const LAST_UPDATED_OPTIONS = [
  { value: "", label: "All" },
  { value: "1month", label: "Last 1 Month" },
  { value: "3months", label: "Last 3 Months" },
  { value: "older", label: "More than 3 Months" },
];

const LAST_VISITED_OPTIONS = [
  { value: "", label: "All" },
  { value: "1month", label: "Last 1 Month" },
  { value: "3months", label: "Last 3 Months" },
  { value: "older", label: "More than 3 Months" },
];

const CV_COMPLETENESS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "80", label: "More than 80%" },
  { value: "60", label: "More than 60%" },
  { value: "40", label: "More than 40%" },
  { value: "20", label: "More than 20%" },
  { value: "0", label: "More than 0%" },
];

const defaultFilters: FilterState = {
  exp_min: "",
  exp_max: "",
  industry_ids: [],
  country_id: "",
  city_id: "",
  education_ids: [],
  language_ids: [],
  nationality_ids: [],
  gender_id: "",
  cv_completeness: "",
  last_updated: "",
  last_visited: "",
  has_video_cv: false,
};

// Collapsible section wrapper
const FilterSection: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="afs-section">
      <button className="afs-section-header" onClick={() => setOpen((v) => !v)}>
        <span className="afs-section-title">{title}</span>
        <span className="afs-section-toggle">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="afs-section-body">{children}</div>}
    </div>
  );
};

const CandidateAdvancedFilters: React.FC<CandidateAdvancedFiltersProps> = ({ onApplyFilters, onResetFilters, onFilterChange }) => {
  const dispatch = useDispatch();

  const [filters, setFilters] = useState<FilterState>(defaultFilters);

  // Notify parent when filters change
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, onFilterChange]);

  const [industries, setIndustries] = useState<Array<{ id: number; name: string }>>([]);
  const [countries, setCountries] = useState<Array<{ id: number; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: number; name: string }>>([]);
  const [educationLevels, setEducationLevels] = useState<Array<{ id: number; name: string }>>([]);
  const [languages, setLanguages] = useState<Array<{ id: number; name: string }>>([]);
  const [nationalities, setNationalities] = useState<Array<{ id: number; name: string }>>([]);

  // Redux selectors for reliable data access
  const industriesRedux = useSelector((state: any) => state.global.industries || []);
  const countriesRedux = useSelector((state: any) => state.global.countriesNew || []);
  const citiesRedux = useSelector((state: any) => state.global.citiesNew || []);
  const educationLevelsRedux = useSelector((state: any) => state.global.educationLevels || []);
  const languagesRedux = useSelector((state: any) => state.global.languages || []);
  const nationalitiesRedux = useSelector((state: any) => state.global.nationalities || []);

  // Sync Redux data to local state when data changes
  useEffect(() => {
    if (industriesRedux?.length > 0) {
      setIndustries(industriesRedux.map((i: any) => ({ id: i.id, name: i.name })));
    }
  }, [industriesRedux]);

  useEffect(() => {
    if (countriesRedux?.length > 0) {
      setCountries(countriesRedux.map((c: any) => ({ id: c.id, name: c.name })));
    }
  }, [countriesRedux]);

  useEffect(() => {
    if (citiesRedux?.length > 0) {
      setCities(citiesRedux.map((c: any) => ({ id: c.id, name: c.name })));
    }
  }, [citiesRedux]);

  useEffect(() => {
    if (educationLevelsRedux?.length > 0) {
      setEducationLevels(educationLevelsRedux.map((e: any) => ({ id: e.id, name: e.name })));
    }
  }, [educationLevelsRedux]);

  useEffect(() => {
    if (languagesRedux?.length > 0) {
      setLanguages(languagesRedux.map((l: any) => ({ id: l.id, name: l.name })));
    }
  }, [languagesRedux]);

  useEffect(() => {
    if (nationalitiesRedux?.length > 0) {
      setNationalities(nationalitiesRedux.map((n: any) => ({ id: n.id, name: n.name })));
    }
  }, [nationalitiesRedux]);

  const [selectedIndustries, setSelectedIndustries] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [selectedCityName, setSelectedCityName] = useState("");
  const [selectedEducations, setSelectedEducations] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedNationalities, setSelectedNationalities] = useState<Array<{ id: number; name: string }>>([]);

  const [countryKey, setCountryKey] = useState(0);
  const [cityKey, setCityKey] = useState(0);
  const [industryKey, setIndustryKey] = useState(0);
  const [educationKey, setEducationKey] = useState(0);
  const [languageKey, setLanguageKey] = useState(0);
  const [nationalityKey, setNationalityKey] = useState(0);

  // Track if filters have been interacted with (to load defaults on focus)
  const [hasIndustryInteracted, setHasIndustryInteracted] = useState(false);
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [hasCityInteracted, setHasCityInteracted] = useState(false);
  const [hasEducationInteracted, setHasEducationInteracted] = useState(false);
  const [hasLanguageInteracted, setHasLanguageInteracted] = useState(false);
  const [hasNationalityInteracted, setHasNationalityInteracted] = useState(false);

  // Debounce refs for search
  const industryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const educationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nationalityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCityDisabled = !filters.country_id;

  // API search functions with q parameter
  const loadIndustries = (q: string = "") => {
    const params: any = { limit: 50, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.INDUSTRIES,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.industries || res?.industries || [];
        setIndustries(data.map((i: any) => ({ id: i.id, name: i.name })));
      },
      callbackError: () => {},
    });
  };

  const loadCountries = (q: string = "") => {
    const params: any = { limit: 50, offset: 0 };
    if (q.trim()) params.name = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.countries || res?.countries || [];
        setCountries(data.map((c: any) => ({ id: c.id, name: c.name })));
      },
      callbackError: () => {},
    });
  };

  const loadCities = (q: string = "") => {
    if (!filters.country_id) return;
    const params: any = { country_id: filters.country_id, limit: 50, offset: 0 };
    if (q.trim()) params.name = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.cities || res?.cities || [];
        setCities(data.map((c: any) => ({ id: c.id, name: c.name })));
      },
      callbackError: () => {},
    });
  };

  const loadEducationLevels = (q: string = "") => {
    const params: any = { limit: 50, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.EDUCATION_LEVELS,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.education_levels || res?.education_levels || res?.data || [];
        setEducationLevels(data.map((e: any) => ({ id: e.id, name: e.name })));
      },
      callbackError: () => {},
    });
  };

  const loadLanguages = (q: string = "") => {
    const params: any = { limit: 50, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.LANGUAGES,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.languages || res?.languages || [];
        setLanguages(data.map((l: any) => ({ id: l.id, name: l.name })));
      },
      callbackError: () => {},
    });
  };

  const loadNationalities = (q: string = "") => {
    const params: any = { limit: 50, offset: 0 };
    if (q.trim()) params.q = q.trim();
    dispatch({
      type: SAGA_ACTIONS.COMMON.NATIONALITIES,
      payload: params,
      callbackSuccess: (res: any) => {
        const data = res?.data?.nationalities || res?.nationalities || [];
        setNationalities(data.map((n: any) => ({ id: n.id, name: n.name })));
      },
      callbackError: () => {},
    });
  };

  // Load all filter data on mount for faster UX
  useEffect(() => {
    loadIndustries();
    loadCountries();
    loadEducationLevels();
    loadLanguages();
    loadNationalities();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (filters.country_id) {
      loadCities();
    } else {
      setCities([]);
    }
  }, [filters.country_id]);

  const handleReset = () => {
    setFilters(defaultFilters);
    setSelectedIndustries([]);
    setSelectedCountryName("");
    setSelectedCityName("");
    setSelectedEducations([]);
    setSelectedLanguages([]);
    setSelectedNationalities([]);
    setCountryKey((k) => k + 1);
    setCityKey((k) => k + 1);
    setIndustryKey((k) => k + 1);
    setEducationKey((k) => k + 1);
    setLanguageKey((k) => k + 1);
    setNationalityKey((k) => k + 1);
    // Reset interaction states
    setHasIndustryInteracted(false);
    setHasCountryInteracted(false);
    setHasCityInteracted(false);
    setHasEducationInteracted(false);
    setHasLanguageInteracted(false);
    setHasNationalityInteracted(false);
    // Clear lists
    setIndustries([]);
    setCountries([]);
    setCities([]);
    setEducationLevels([]);
    setLanguages([]);
    setNationalities([]);
    onResetFilters();
  };

  const handleApply = () => {
    onApplyFilters(filters);
  };

  const removeIndustry = (id: number) => {
    const updated = selectedIndustries.filter((i) => i.id !== id);
    setSelectedIndustries(updated);
    setFilters((f) => ({ ...f, industry_ids: updated.map((i) => i.id) }));
    setIndustryKey((k) => k + 1);
  };
  const removeEducation = (id: number) => {
    const updated = selectedEducations.filter((e) => e.id !== id);
    setSelectedEducations(updated);
    setFilters((f) => ({ ...f, education_ids: updated.map((e) => e.id) }));
    setEducationKey((k) => k + 1);
  };
  const removeLanguage = (id: number) => {
    const updated = selectedLanguages.filter((l) => l.id !== id);
    setSelectedLanguages(updated);
    setFilters((f) => ({ ...f, language_ids: updated.map((l) => l.id) }));
    setLanguageKey((k) => k + 1);
  };
  const removeNationality = (id: number) => {
    const updated = selectedNationalities.filter((n) => n.id !== id);
    setSelectedNationalities(updated);
    setFilters((f) => ({ ...f, nationality_ids: updated.map((n) => n.id) }));
    setNationalityKey((k) => k + 1);
  };

  const acStyle = { height: "36px", border: "1px solid #d1d5db", borderRadius: "6px", boxShadow: "none", zIndex: 9999, fontSize: "13px" };

  return (
    <div className="advanced-search-filters">
      {/* Scrollable Content */}
      <div className="afs-scrollable">
        {/* Header */}
        <div className="afs-header">
          <span className="afs-title"><i className="fa fa-sliders-h"></i> Filters</span>
          <button className="afs-clear-btn" onClick={handleReset}>Clear all</button>
        </div>

        {/* CV Activity */}
        <FilterSection title="CV Activity">
        <div className="afs-subsection">
          <div className="afs-sublabel">Last Updated</div>
          {LAST_UPDATED_OPTIONS.map((opt) => (
            <label key={opt.value} className="afs-radio">
              <input type="radio" name="last_updated" value={opt.value}
                checked={filters.last_updated === opt.value}
                onChange={() => setFilters((f) => ({ ...f, last_updated: opt.value }))} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        <div className="afs-subsection">
          <div className="afs-sublabel">Last Visited</div>
          {LAST_VISITED_OPTIONS.map((opt) => (
            <label key={opt.value} className="afs-radio">
              <input type="radio" name="last_visited" value={opt.value}
                checked={filters.last_visited === opt.value}
                onChange={() => setFilters((f) => ({ ...f, last_visited: opt.value }))} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Years of Experience */}
      <FilterSection title="Years of Experience">
        <div className="afs-exp-row">
          <div className="afs-exp-field">
            <label>Min</label>
            <input type="number" min="0" max="50" placeholder="0" className="afs-input"
              value={filters.exp_min}
              onChange={(e) => setFilters((f) => ({ ...f, exp_min: e.target.value }))} />
          </div>
          <span className="afs-exp-sep">–</span>
          <div className="afs-exp-field">
            <label>Max</label>
            <input type="number" min="0" max="50" placeholder="50" className="afs-input"
              value={filters.exp_max}
              onChange={(e) => setFilters((f) => ({ ...f, exp_max: e.target.value }))} />
          </div>
        </div>
      </FilterSection>

      {/* Industry */}
      <FilterSection title="Industry">
        <ReactSearchAutocomplete key={`ind-${industryKey}`} items={industries.filter((i) => !filters.industry_ids.includes(i.id))}
          onSelect={(item: any) => {
            if (!filters.industry_ids.includes(item.id)) {
              const updated = [...selectedIndustries, { id: item.id, name: item.name }];
              setSelectedIndustries(updated);
              setFilters((f) => ({ ...f, industry_ids: updated.map((i) => i.id) }));
              setIndustryKey((k) => k + 1);
            }
          }}
          onSearch={(string) => {
            setHasIndustryInteracted(true);
            if (industryDebounceRef.current) clearTimeout(industryDebounceRef.current);
            industryDebounceRef.current = setTimeout(() => { loadIndustries(string); }, 300);
          }}
          onFocus={() => { if (!hasIndustryInteracted) { setHasIndustryInteracted(true); loadIndustries(); } }}
          placeholder="Search industry..." styling={acStyle} showIcon={false} autoFocus={false} />
        <div className="afs-tags">
          {selectedIndustries.map((i) => (
            <span key={i.id} className="afs-tag">{i.name}<button onClick={() => removeIndustry(i.id)}>×</button></span>
          ))}
        </div>
      </FilterSection>

      {/* Country */}
      <FilterSection title="Country">
        <ReactSearchAutocomplete key={`cou-${countryKey}`} items={countries}
          onSelect={(item: any) => {
            setSelectedCountryName(item.name);
            setFilters((f) => ({ ...f, country_id: String(item.id), city_id: "" }));
            setSelectedCityName("");
            setCityKey((k) => k + 1);
          }}
          onSearch={(string) => {
            setHasCountryInteracted(true);
            if (countryDebounceRef.current) clearTimeout(countryDebounceRef.current);
            countryDebounceRef.current = setTimeout(() => { loadCountries(string); }, 300);
          }}
          onFocus={() => { if (!hasCountryInteracted) { setHasCountryInteracted(true); loadCountries(); } }}
          onClear={() => {
            setSelectedCountryName("");
            setFilters((f) => ({ ...f, country_id: "", city_id: "" }));
            setSelectedCityName(""); setCities([]); setCityKey((k) => k + 1);
          }}
          placeholder="Search country..." inputSearchString={selectedCountryName}
          styling={acStyle} showIcon={false} autoFocus={false} />
      </FilterSection>

      {/* City */}
      <FilterSection title="City">
        {isCityDisabled ? (
          <input type="text" className="afs-input afs-input-disabled" placeholder="Select country first" disabled />
        ) : (
          <ReactSearchAutocomplete key={`cit-${cityKey}`} items={cities}
            onSelect={(item: any) => { setSelectedCityName(item.name); setFilters((f) => ({ ...f, city_id: String(item.id) })); }}
            onSearch={(string) => {
              setHasCityInteracted(true);
              if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
              cityDebounceRef.current = setTimeout(() => { loadCities(string); }, 300);
            }}
            onFocus={() => { if (!hasCityInteracted) { setHasCityInteracted(true); loadCities(); } }}
            onClear={() => { setSelectedCityName(""); setFilters((f) => ({ ...f, city_id: "" })); }}
            placeholder="Search city..." inputSearchString={selectedCityName}
            styling={acStyle} showIcon={false} autoFocus={false} />
        )}
      </FilterSection>

      {/* Education Level */}
      <FilterSection title="Education Level">
        <ReactSearchAutocomplete key={`edu-${educationKey}`} items={educationLevels.filter((e) => !filters.education_ids.includes(e.id))}
          onSelect={(item: any) => {
            if (!filters.education_ids.includes(item.id)) {
              const updated = [...selectedEducations, { id: item.id, name: item.name }];
              setSelectedEducations(updated);
              setFilters((f) => ({ ...f, education_ids: updated.map((e) => e.id) }));
              setEducationKey((k) => k + 1);
            }
          }}
          onSearch={(string) => {
            setHasEducationInteracted(true);
            if (educationDebounceRef.current) clearTimeout(educationDebounceRef.current);
            educationDebounceRef.current = setTimeout(() => { loadEducationLevels(string); }, 300);
          }}
          onFocus={() => { if (!hasEducationInteracted) { setHasEducationInteracted(true); loadEducationLevels(); } }}
          placeholder="Search education..." styling={acStyle} showIcon={false} autoFocus={false} />
        <div className="afs-tags">
          {selectedEducations.map((e) => (
            <span key={e.id} className="afs-tag">{e.name}<button onClick={() => removeEducation(e.id)}>×</button></span>
          ))}
        </div>
      </FilterSection>

      {/* Language */}
      <FilterSection title="Language">
        <ReactSearchAutocomplete key={`lan-${languageKey}`} items={languages.filter((l) => !filters.language_ids.includes(l.id))}
          onSelect={(item: any) => {
            if (!filters.language_ids.includes(item.id)) {
              const updated = [...selectedLanguages, { id: item.id, name: item.name }];
              setSelectedLanguages(updated);
              setFilters((f) => ({ ...f, language_ids: updated.map((l) => l.id) }));
              setLanguageKey((k) => k + 1);
            }
          }}
          onSearch={(string) => {
            setHasLanguageInteracted(true);
            if (languageDebounceRef.current) clearTimeout(languageDebounceRef.current);
            languageDebounceRef.current = setTimeout(() => { loadLanguages(string); }, 300);
          }}
          onFocus={() => { if (!hasLanguageInteracted) { setHasLanguageInteracted(true); loadLanguages(); } }}
          placeholder="Search language..." styling={acStyle} showIcon={false} autoFocus={false} />
        <div className="afs-tags">
          {selectedLanguages.map((l) => (
            <span key={l.id} className="afs-tag">{l.name}<button onClick={() => removeLanguage(l.id)}>×</button></span>
          ))}
        </div>
      </FilterSection>

      {/* Nationality */}
      <FilterSection title="Nationality">
        <ReactSearchAutocomplete key={`nat-${nationalityKey}`} items={nationalities.filter((n) => !filters.nationality_ids.includes(n.id))}
          onSelect={(item: any) => {
            if (!filters.nationality_ids.includes(item.id)) {
              const updated = [...selectedNationalities, { id: item.id, name: item.name }];
              setSelectedNationalities(updated);
              setFilters((f) => ({ ...f, nationality_ids: updated.map((n) => n.id) }));
              setNationalityKey((k) => k + 1);
            }
          }}
          onSearch={(string) => {
            setHasNationalityInteracted(true);
            if (nationalityDebounceRef.current) clearTimeout(nationalityDebounceRef.current);
            nationalityDebounceRef.current = setTimeout(() => { loadNationalities(string); }, 300);
          }}
          onFocus={() => { if (!hasNationalityInteracted) { setHasNationalityInteracted(true); loadNationalities(); } }}
          placeholder="Search nationality..." styling={acStyle} showIcon={false} autoFocus={false} />
        <div className="afs-tags">
          {selectedNationalities.map((n) => (
            <span key={n.id} className="afs-tag">{n.name}<button onClick={() => removeNationality(n.id)}>×</button></span>
          ))}
        </div>
      </FilterSection>

      {/* Gender */}
      <FilterSection title="Gender">
        {[{ value: "", label: "Any" }, { value: "1", label: "Male" }, { value: "2", label: "Female" }].map((opt) => (
          <label key={opt.value} className="afs-radio">
            <input type="radio" name="gender_id" value={opt.value}
              checked={filters.gender_id === opt.value}
              onChange={() => setFilters((f) => ({ ...f, gender_id: opt.value }))} />
            <span>{opt.label}</span>
          </label>
        ))}
      </FilterSection>

      {/* CV Completeness */}
      <FilterSection title="CV Completeness">
        {CV_COMPLETENESS_OPTIONS.map((opt) => (
          <label key={opt.value} className="afs-radio">
            <input type="radio" name="cv_completeness" value={opt.value}
              checked={filters.cv_completeness === opt.value}
              onChange={() => setFilters((f) => ({ ...f, cv_completeness: opt.value }))} />
            <span>{opt.label}</span>
          </label>
        ))}
      </FilterSection>

      {/* Video CV */}
      <FilterSection title="Video CV">
        <label className="afs-checkbox">
          <input type="checkbox" checked={filters.has_video_cv}
            onChange={(e) => setFilters((f) => ({ ...f, has_video_cv: e.target.checked }))} />
          <span>Has Video CV</span>
        </label>
      </FilterSection>
      </div>

      {/* Apply - Fixed at bottom */}
      <div className="afs-apply-wrap">
        <button className="afs-apply-btn" onClick={handleApply}>Apply Filters</button>
      </div>
    </div>
  );
};

export default CandidateAdvancedFilters;
